import { Component, Prop, Ref, SimpleEvent, Watch } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';
import { setVariantUrlParam } from '@/utils/products';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';
import { QuantitySelector } from '@/components/QuantitySelector/QuantitySelector';
import { formatMoney } from '@/utils/formatting';
import { throttle } from 'lodash';

@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop defaultVariant!: Shopify.Liquid.Variant;
    @Prop optionStyle!: 'dropdown'|'buttons';
    @Prop({default: false}) isFrameset!: boolean;

    @Ref({required: false}) quantitySelector!: QuantitySelector;
    @Ref({required: false}) options!: ProductOptionPicker;
    @Ref({required: false}) additionalOptionChoice!: HTMLInputElement[];
    @Ref({required: false}) additionalOptionInput!: HTMLInputElement;
    @Ref({required: false}) actions!: HTMLDivElement;
    @Ref({required: false}) submitButton!: HTMLButtonElement;
    @Ref({required: false}) framesetTriggerButton!: HTMLButtonElement;
    @Ref({required: false}) popularBuildTriggerButton!: HTMLButtonElement;
    @Ref backInStockTrigger !: HTMLButtonElement;
    @Ref error!: HTMLElement;
    @Ref errorMessage!: HTMLElement;

    @Ref({ required: false }) giftCheckbox!: HTMLInputElement;
    @Ref({ required: false }) recipientEmail!: HTMLInputElement;
    @Ref({ required: false }) recipientName!: HTMLInputElement;
    @Ref({ required: false }) recipientMessage!: HTMLInputElement;
    @Ref({ required: false }) recipientDate!: HTMLInputElement;
    @Ref({ required: false }) recipientMessageRemainingValue!: HTMLSpanElement;

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant|null = null;
    protected quantity: number = 1;

    protected giftcardFormNamePresent = false;
    protected giftcardFormEmailPresent = false;


    init() {
        // Check variant availability
        this.selectedVariant = this.defaultVariant;
        this.checkVariantAvailability(this.selectedVariant);

        // Listen for quantity updates
        if (this.quantitySelector) {
            this.$subscribe(this.quantitySelector.onChange, (quantity) => this.quantity = quantity);
        }

        // Listen for additional option changes
        if (this.additionalOptionChoice && this.additionalOptionInput) {
            this.handleAdditionalChoices();
        }

        // Listen for form submission
        if (this.submitButton) {
            this.$listen(this.submitButton, 'click', () => {
                this.addToCart().catch((e) => {
                    this.$log.error(`Add to cart failed`, { e });
                });
                // if quick add to cart is in cart page
                if(this.submitButton.classList.contains('product-form__submit--cartPage')) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setTimeout(() => {
                        location.reload(); // Reload the page after scrolling to the top
                    }, 300);
                }
            });
            document.dispatchEvent(new Event('cartChanged'));
        }

        // Validate gift recipient message
        if (this.recipientMessage) {
            this.$listen(this.recipientMessage, 'keyup', (event) => {
                if (this.recipientMessageRemainingValue) {
                    this.recipientMessageRemainingValue.innerText = `${200 - this.recipientMessage.value.length}`;
                }
            });
        }

        this.handleVariantPrice(this.selectedVariant);
        this.handleVariantLink(this.selectedVariant);

        // Hide errors on input
        this.hideErrorsOnInput();
    }

    protected showError(msg: string|null): void {
        this.errorMessage.textContent = msg ? msg.split('.')[0] : '';
        if (msg) {
            this.error.classList.add('product-form__error--active');
        } else {
            this.error.classList.remove('product-form__error--active');
        }
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        if (this.classList.contains('product-form--card')) { return; } // if product card
        setVariantUrlParam(this.selectedVariant?.id || null);
    }

    @Watch('loading')
    protected handleLoading() {
        if (this.submitButton) {
            this.submitButton.disabled = this.loading;
        }
    }

    private _onSelectedPropertyChange = new SimpleEvent<string>();
    public get onSelectedPropertyChange() {
        return this._onSelectedPropertyChange.asEvent();
    }

    checkVariantAvailability(variant: Shopify.Liquid.Variant|null) {
        // Check variant is defined
        if (variant === null) {

            // Disable
            this.updateActionsVisibility(true);
            return;
        }

        // Check variant is in stock
        if (!variant.available) {

            // Get variant inventory quantity
            let variantInventoryQuantity = 0;
            if (variant.inventory_quantity) {
                variantInventoryQuantity = variant.inventory_quantity;
            }

            // Disable
            this.updateActionsVisibility(true, variantInventoryQuantity);
            return;
        }

        // Enable
        this.updateActionsVisibility(false);
    }

    handleAdditionalChoices() {
        for (const additionalOptionChoice of this.additionalOptionChoice) {
            this.$listen(additionalOptionChoice, 'change', () => {

                // Update active class
                for (const choice of this.additionalOptionChoice) {
                    const $wrapper = choice.parentNode as HTMLElement;
                    if (choice.checked) {
                        $wrapper.classList.add('product-form__button-item--active');
                    } else {
                        $wrapper.classList.remove('product-form__button-item--active');
                    }
                }

                // Update hidden input value
                const value = additionalOptionChoice.value || null;
                if (value) {
                    this.additionalOptionInput.value = value;
                }

                // Dispatch event
                if (value) {
                    this._onSelectedPropertyChange.dispatch(value);
                }
            });
        }
    }

    updateActionsVisibility(disabled: boolean, variantInventoryQuantity: number = 0) {
        if (disabled) {

            // Submit button
            if (this.submitButton) {

                // Disable button
                this.submitButton.classList.add('product-form__submit--disabled');

                // Update unavailable/pre-order text
                let submitButtonText = 'Out Of Stock';
                if (this.product.tags && this.product.tags.includes('preorder') && variantInventoryQuantity <= 0) {
                    const preorderText = this.submitButton.getAttribute('data-preorder-text');
                    if (preorderText) {
                        submitButtonText = preorderText;
                    }
                } else {
                    const unavailableText = this.submitButton.getAttribute('data-unavailable-text');
                    if (unavailableText) {
                        submitButtonText = unavailableText;
                    }
                }
                this.submitButton.textContent = submitButtonText;
            }

            // Hide actions
            if (this.actions) {
                this.actions.classList.add('product-form__actions--hidden');
            }

            // Show back in stock trigger
            if (this.backInStockTrigger) {
                this.backInStockTrigger.classList.add('product-form__back-in-stock-trigger--active');
            }

            // Update frameset trigger button state
            if (this.framesetTriggerButton) {
                const unavailableText = this.framesetTriggerButton.getAttribute('data-unavailable-text');
                if (unavailableText) {
                    this.framesetTriggerButton.innerHTML = unavailableText;
                }
                this.framesetTriggerButton.classList.add('button--disabled');
            }
        } else {

            // Submit button
            if (this.submitButton) {

                // Enable button
                this.submitButton.classList.remove('product-form__submit--disabled');

                // Update available/pre-order text
                let submitButtonText = 'Add To Cart';
                if (this.product.tags && this.product.tags.includes('preorder') && variantInventoryQuantity <= 0) {
                    const preorderText = this.submitButton.getAttribute('data-preorder-text');
                    if (preorderText) {
                        submitButtonText = preorderText;
                    }
                } else {
                    const availableText = this.submitButton.getAttribute('data-available-text');
                    if (availableText) {
                        submitButtonText = availableText;
                    }
                }
                this.submitButton.textContent = submitButtonText;
            }

            // Show actions
            if (this.actions) {
                this.actions.classList.remove('product-form__actions--hidden');
            }

            // Hide back in stock trigger
            if (this.backInStockTrigger) {
                this.backInStockTrigger.classList.remove('product-form__back-in-stock-trigger--active');
            }

            // Update frameset trigger button state
            if (this.framesetTriggerButton) {
                const availableText = this.framesetTriggerButton.getAttribute('data-available-text');
                if (availableText) {
                    this.framesetTriggerButton.innerHTML = availableText;
                }
                this.framesetTriggerButton.classList.remove('button--disabled');
            }
        }
    }

    handleVariantPrice(variant: Shopify.Liquid.Variant | null) {
        if (this.options) {
            this.$subscribe(this.options.onSelectedVariantChange, (variant) => {
                if (variant === null) {
                    return;
                }
                this.selectedVariant = variant;

                // Check variant availability
                this.checkVariantAvailability(this.selectedVariant);
                const productDetails = this.closest('.product-wrap-js');

                // Update price
                if (productDetails) {
                    const priceContainers = productDetails.querySelectorAll('.product-details__price--normal');
                    for (const priceContainer of priceContainers) {
                        priceContainer.innerHTML = formatMoney(variant.price);
                    }
                    // eslint-disable-next-line max-len
                    const framesetPriceContainer = productDetails.querySelector('.product-frameset-details__price--value');
                    const framesetOverlayPriceCont = productDetails.querySelector('.frameset-overlay-subtotal__value');

                    if (framesetPriceContainer) {
                        framesetPriceContainer.innerHTML = formatMoney(variant.price);
                    }

                    if (framesetOverlayPriceCont) {
                        framesetOverlayPriceCont.innerHTML = formatMoney(variant.price);
                    }
                }

                if (productDetails) {
                    const comparePriceContainers = productDetails.querySelectorAll('.product-details__price--compare');
                    for (const comparePriceContainer of comparePriceContainers) {
                        if (variant.compare_at_price) {
                            comparePriceContainer.innerHTML = formatMoney(variant.compare_at_price);
                            if (this.submitButton) {
                                // update data attribute on ATC to add sale price to side cart
                                this.submitButton.dataset.comparePrice = variant.compare_at_price.toString();
                            }
                            comparePriceContainer.classList.remove('visually-hidden');
                        } else {
                            comparePriceContainer.classList.add('visually-hidden');
                            this.submitButton.dataset.comparePrice = '';
                        }
                    }
                    // eslint-disable-next-line max-len
                    const framesetComparePriceCont = productDetails.querySelector('.product-frameset-details__price--compare');
                    // eslint-disable-next-line max-len
                    const fsOverlayComparePriceCont = productDetails.querySelector('.frameset-overlay-subtotal__value--compare');

                    if (framesetComparePriceCont) {
                        if (variant.compare_at_price) {
                            framesetComparePriceCont.innerHTML = formatMoney(variant.compare_at_price);
                            framesetComparePriceCont.classList.remove('visually-hidden');
                        } else {
                            framesetComparePriceCont.classList.add('visually-hidden');
                        }
                    }

                    if (fsOverlayComparePriceCont) {
                        if (variant.compare_at_price) {
                            fsOverlayComparePriceCont.innerHTML = formatMoney(variant.compare_at_price);
                            fsOverlayComparePriceCont.classList.remove('visually-hidden');
                        } else {
                            fsOverlayComparePriceCont.classList.add('visually-hidden');
                        }
                    }
                }

                // Hide error
                this.error.classList.remove('product-form__error--active');
            });
        }
    }

    handleVariantLink(variant: Shopify.Liquid.Variant | null) {
        if (this.options) {
            this.$subscribe(this.options.onSelectedVariantChange, (variant) => {
                if (variant === null) {
                    return;
                }
                this.selectedVariant = variant;
                const variantLink = this.closest('.product-wrap-js');
                if (variantLink) {
                    const linkElement = variantLink.querySelector<HTMLAnchorElement>('.pdp-variant-link');
                    if (linkElement && this.selectedVariant) {
                        // Get the current href value
                        const currentHref = linkElement.href;
                        const variantParam = `variant=${this.selectedVariant.id}`;

                        // Check if the href already contains a variant parameter
                        if (currentHref.includes('variant=')) {
                            // If it contains the 'variant' parameter, update it
                            linkElement.href = currentHref.replace(/variant=\d+/, variantParam);
                        } else {
                            // If the 'variant' parameter doesn't exist, append it
                            linkElement.href = `${currentHref}?${variantParam}`;
                        }
                    }
                }
            });
        }
    }


    protected async addToCart() {

        // Check gift card conditions - do not continue if they fail
        let giftCardConditionsPassed = true;
        if (this.giftCheckbox) {
            giftCardConditionsPassed = await this.checkGiftCardConditions();
        }
        if (!giftCardConditionsPassed) {
            return;
        }

        try {
            this.loading = true;

            // Check variant exists
            const variant = this.selectedVariant;
            if (!variant) {
                this.showError(`Please select an option.`);
                return;
            }

            // Define line item properties shape
            const properties: {[key: string]: string} = {};

            // Force Zapiet properties
            // properties['_ZapietId'] = 'M=S';

            // Check if pre-order tag exists and variant does not have stock - add proerty if true
            if (this.product.tags.includes('preorder') && variant.inventory_quantity <= 0) {
                properties['_preorder'] = 'true';
            }

            // Add compare price to line item properties
            if (Number(this.submitButton.dataset.comparePrice) > this.product.price) {
                properties['_compare'] = String(this.submitButton.dataset.comparePrice);
            }

            // Check if gift checkbox checked
            if (this.giftCheckbox && this.giftCheckbox.checked) {
                properties['__shopify_send_gift_card_to_recipient'] = this.giftCheckbox.value;
                properties['Recipient email'] = this.recipientEmail.value;
                properties['Recipient name'] = this.recipientName.value;
                properties['Send on'] = this.recipientDate.value;
                properties['Message'] = this.recipientMessage.value;
            }

            // Add to cart
            await cart.addItem({
                id: variant.id,
                quantity: this.quantity,
                properties: properties
            });

            // Dispatch event for other components to listen to
            document.dispatchEvent(new Event('cartOpen'));
        } catch (event) {
            this.$log.error('Cart error', {
                event
            });
            const { message } = event as Error;
            this.showError(message);
        } finally {
            this.loading = false;
        }
    }

    private async checkGiftCardConditions() {

        // Assume conditions pass
        let conditionsPass = true;

        // Check if gift checkbox checked
        if (this.giftCheckbox.checked) {

            // Check recipient name is entered
            if (this.recipientName.value.length < 1) {

                // Tell function conditional checks have failed
                conditionsPass = false;

                // Check for previous error not removed, create new error
                const existingError = document.querySelector('.field--error-name');
                if (existingError === null) {
                    const errorElement = document.createElement('span');
                    errorElement.classList.add('field--error-name');
                    errorElement.textContent = 'Please enter the recipients name';
                    this.recipientName.after(errorElement);
                }
            }

            // Check recipient email is entered
            if (this.recipientEmail.value.length < 1) {

                // Tell function conditional checks have failed
                conditionsPass = false;

                // Check for previous error not removed, create new error
                const existingError = document.querySelector('.field--error-email');
                if (existingError === null) {
                    const errorElement = document.createElement('span');
                    errorElement.classList.add('field--error-email');
                    errorElement.textContent = 'Please enter the recipients email';
                    this.recipientEmail.after(errorElement);
                }
            }
        }

        // Return value
        return conditionsPass;
    }

    private hideErrorsOnInput() {

        // Define an array of inputs that will have associated errors
        const inputs = [
            this.recipientEmail,
            this.recipientName
        ];

        // Loop through inputs, listen for change
        for (const input of inputs) {
            if(input != undefined) {
                this.$listen(input, 'input', throttle(() => {
                    const error = input.parentNode?.querySelector('[class*="field--error"]');
                    if (error) {
                        error.remove();
                    }
                }, 500));
            }
        }
    }
}
